import { IOrderClause } from '@tableau/catalog-ui-temp';
import { Action } from 'redux';
import _ from 'underscore';

import { ContentFavoriteAdded, ContentFavoriteRemoved, RecommendationHidden } from '~src/bootstrap/redux/actions/AppActionTypes';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { isSetCollectionVisibility } from '~src/Collections/actions/SetCollectionVisibilityAction';
import { isRemoveDraftFromContentInfoAction } from '~src/drafts/actions/DiscardDraftAction';
import { ContainerType, WorkbookDraftContent, WorkbookDraftContentType } from '~src/drafts/types/DraftsTypes';
import {
  containerTypeToLocationType,
  getSortedDraftsAndContent,
  isPublishedDraft,
  isUnpublishedDraft,
  populateLuidToNameAndIDMaps,
  removeUnpublishedDraftsFromContent
} from '~src/drafts/utils/DraftUtils';
import { isContentFavoriteAddedAction, isContentFavoriteRemovedAction } from '~src/favorites/actions/FavoritesActions';
import {
  isRecommendationHiddenAction,
  isRecommendationUnhiddenAction
} from '~src/recommendations/actions/HideRecommendationAction';
import { ExplorableItemType, isRecommendationType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IExplorableItemTypeInfo, IFavoriteItem } from '~src/spacesPlatform/models/resourceInfoParts';
import { selectContentSort } from '~src/spacesPlatform/selectors/ContentSortSelectors';
import { isFavoritesSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { IViewInfo } from '~src/views/models/IViewInfo';

import { isUpdateContentDetailAction } from '../../actions/GetContentDetailAction';
import { isReorderItemAction } from '../../actions/ReorderItem';
import {
  isSpaceContentsNextPageWithTokenSuccededAction,
  isSpaceContentsRequestFailedAction,
  isSpaceContentsRequestSucceededAction,
  isSpaceContentsSliceSucceededAction,
  isSpaceContentsUpdateOwnersAction,
  isSpaceContentsWithTokenRequestSucceededAction
} from '../../actions/RequestSpaceContentsActions';

function findIndexOfContentById(content: ReadonlyArray<IExplorableItemTypeInfo>, type: string, id: string): number {
  return _.findIndex(content, c => c?.contentType === type && c.id === id);
}

function findIndexOfContentByLuid(content: ReadonlyArray<IExplorableItemTypeInfo>, type: string, id: string): number {
  return _.findIndex(content, c => c?.contentType === type && c.luid === id);
}

function updateContent(
  content: ReadonlyArray<IExplorableItemTypeInfo>,
  type: ExplorableItemType,
  id: string,
  updateOptions: any,
  isLuid: boolean = false,
): ReadonlyArray<IExplorableItemTypeInfo> {
  const index = isLuid ? findIndexOfContentByLuid(content, type, id) : findIndexOfContentById(content, type, id);
  if (index > -1) {
    const newContent = content.slice(0);
    newContent[index] = {
      ...content[index],
      ...updateOptions
    };
    return newContent;
  }
  return content;
}

// exported for testing
export function updateContentForPublishedWorkbookDrafts(
  content: ReadonlyArray<IExplorableItemTypeInfo>,
  workbookDrafts: WorkbookDraftContent[]
): ReadonlyArray<IExplorableItemTypeInfo> {
  const isLuidFlag: boolean = true;
  for (const workbookDraft of workbookDrafts) {
    if (isPublishedDraft(workbookDraft.draftStatus)) {
      content = updateContent(
        content,
        WorkbookDraftContentType,
        workbookDraft.id,
        {
          draftLuid: workbookDraft.draftLuid,
          draftStatus: workbookDraft.draftStatus,
        },
        isLuidFlag
      );
    }
  }
  return content;
}

// exported for testing
export function updateContentForUnpublishedWorkbookDrafts(
  content: ReadonlyArray<IExplorableItemTypeInfo>,
  workbookDrafts: WorkbookDraftContent[],
): ReadonlyArray<IExplorableItemTypeInfo> {
  let unpublishedWorkbookDrafts = workbookDrafts.filter(draft => isUnpublishedDraft(draft.draftStatus));
  if (unpublishedWorkbookDrafts.length === 0) {
    return content;
  }
  const order = selectContentSort(getAppStore().getState()) as IOrderClause[];

  // make maps of containerluid to containername and ID
  let luidToName = new Map<string, string>();
  let luidToID = new Map<string, string>();
  populateLuidToNameAndIDMaps(content, luidToName, luidToID);

  // populate drafts 'location' field if possible using luid maps
  for (const draft of workbookDrafts) {
    if (luidToName.has(draft.containerLuid)) {
      draft.location = {
        id: draft.containerType === ContainerType.PersonalSpace ? 'personalSpace' : luidToID.get(draft.containerLuid),
        name: luidToName.get(draft.containerLuid),
        type: containerTypeToLocationType(draft.containerType),
        luid: draft.containerLuid
      };
    }
  }

  return getSortedDraftsAndContent(content, unpublishedWorkbookDrafts, order);
}

// exported for testing
export function updateContentForWorkbookDrafts(
  content: ReadonlyArray<IExplorableItemTypeInfo>,
  workbookDrafts: WorkbookDraftContent[]
): ReadonlyArray<IExplorableItemTypeInfo> {
  const updatedContent = updateContentForPublishedWorkbookDrafts(content, workbookDrafts);
  return updateContentForUnpublishedWorkbookDrafts(updatedContent, workbookDrafts);
}

export function contentReducer(
  previousState: ReadonlyArray<IExplorableItemTypeInfo> = [],
  action: Action = null
): ReadonlyArray<IExplorableItemTypeInfo> {
  if (isSpaceContentsRequestSucceededAction(action) && action.content) {
    if (action.workbookDrafts) {
      // Don't set a new empty array as that will trigger renders.
      if (action.content.items.length === 0 && action.workbookDrafts.length === 0 && previousState.length === 0) {
        return previousState;
      }

      // We begin anew assuming the slice starts with zero index, so extend the items array to match total
      const content = action.content.items.concat(new Array(action.content.total - action.content.items.length));
      return updateContentForWorkbookDrafts(content, action.workbookDrafts);
    }

    // Don't set a new empty array as that will trigger renders.
    if (action.content.items.length === 0 && previousState.length === 0) {
      return previousState;
    }

    // We ignore any items in previous state and begin anew
    // We assume the slice starts with zero index so just extend
    // items array to match the total
    const emptyMissingItems =
      new Array(action.content.total - action.content.items.length);
    return action.content.items.concat(emptyMissingItems);

  } else if (isSpaceContentsSliceSucceededAction(action) && action.content) {
    if (action.workbookDrafts) {
      const previousStateFiltered = removeUnpublishedDraftsFromContent(previousState);

      let items: IExplorableItemTypeInfo[];
      if (previousStateFiltered.length === action.content.total || action.appendOnly) {
        items = previousStateFiltered.slice();
      } else {
        items = new Array(action.content.total);
      }
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return updateContentForWorkbookDrafts(items, action.workbookDrafts);
    }

    if (previousState.length === action.content.total || action.appendOnly) {
      // if total items hasn't changed insert  newly retrieved items
      // into our existing items
      const items = previousState.slice();
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return items;
    } else {
      // Total number of items doesn't match our state total
      // So we start over and insert our slice
      const items = new Array(action.content.total);
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return items;
    }

  } else if (isSpaceContentsWithTokenRequestSucceededAction(action) && action.contentWithToken) {
    // Don't set a new empty array as that will trigger renders.
    if (action.contentWithToken.items.length === 0 && previousState.length === 0) {
      return previousState;
    }
    return action.contentWithToken.items;
  } else if (isSpaceContentsNextPageWithTokenSuccededAction(action) && action.contentWithToken) {

    return previousState.concat(action.contentWithToken.items);
  } else if (isContentFavoriteAddedAction(action) || isContentFavoriteRemovedAction(action)) {
    // if in Recommendation space page, keep favorite states of RecommendationCards updated
    if (previousState.length > 0 && isRecommendationType(previousState[0].contentType)) {
      let content = _.find(previousState, c => c.id === action.item.id);
      if (action.type === ContentFavoriteAdded && !(content as IContentInfo).favorite) {
        (content as IViewInfo).usageInfo.favoritesTotal += 1;
      } else if (action.type === ContentFavoriteRemoved && (content as IContentInfo).favorite) {
        (content as IViewInfo).usageInfo.favoritesTotal -= 1;
      }

      return updateContent(previousState, 'recommendedView', action.item.id, {
        favorite: action.type === ContentFavoriteAdded,
      });
    }

    return updateContent(previousState, action.item.contentType, action.item.id, {
      favorite: action.type === ContentFavoriteAdded
    });
  } else if (isRemoveDraftFromContentInfoAction(action)) {
    let contentId = action.contentId.toString();
    const isLuidFlag: boolean = true;
    return updateContent(
      previousState,
      action.contentType,
      contentId,
      {
        draftLuid: null, draftStatus: null
      },
      isLuidFlag
    );
  } else if (isRecommendationHiddenAction(action) || isRecommendationUnhiddenAction(action)) {
    let contentId = action.contentId;
    let contentType = action.contentType;

    return updateContent(previousState, contentType, contentId, {
      hide: action.type === RecommendationHidden
    });

  } else if (isUpdateContentDetailAction(action)) {
    let updatedContent = previousState;
    for (let content of action.content) {
      updatedContent = updateContent(updatedContent, content.contentType, content.id, { ...content });
    }
    return updatedContent;
  } else if (isSetCollectionVisibility(action)) {
    let updatedContent = previousState;
    for (let contentId of action.contentIds) {
      updatedContent = updateContent(updatedContent, 'collection', contentId, {
        visibility: action.visibility
      });
    }
    return updatedContent;
  } else if (isSpaceContentsUpdateOwnersAction(action)) {
    let updatedContent = previousState;
    for (let content of action.contents) {
      const updatedOwner = { owner: action.owners[content.ownerId] };
      updatedContent = updateContent(updatedContent, content.contentType, content.contentId, { ...updatedOwner });
    }
    return updatedContent;
  } else if (isReorderItemAction(action)) {
    let items: Array<IExplorableItemTypeInfo> = [];

    if (isFavoritesSpace(action.spaceInfo)) {
      // In the favorite space, we need to filter out non-favorited items. This happens when a user unfavorites an item
      // from the favorites space-- we keep it in the store in case they want to undo that action. To make sure the
      // reordering works correctly, we only want to operate on items that are showing in the ui (favorited items)
      previousState.forEach(item => {
        if ((item as unknown as IFavoriteItem).favorite) {
          items.push(item);
        }
      });

    } else {
      items = previousState.slice(0);
    }

    const toIndex = Math.max(0, action.indexToMoveAfter + 1);
    if (action.fromIndex > toIndex) {
      items.splice(toIndex, 0, items.splice(action.fromIndex, 1)[0]);
    } else if (action.fromIndex < toIndex) {
      items.splice(toIndex, 0, items[action.fromIndex]);
      items.splice(action.fromIndex, 1);
    }
    return items;
  } else if (isSpaceContentsRequestFailedAction(action)) {
    // Don't set a new empty array as that will trigger renders.
    if (previousState.length === 0) {
      return previousState;
    }

    return [];
  }

  return previousState;
}
